import React, { useState, useCallback, useEffect } from 'react'
import { Modal } from '../ui/Modal'
import { Button } from '../ui/Button'
import { 
  Upload,
  FileText,
  X,
  CheckCircle
} from 'lucide-react'

interface UploadScopeModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: { projectName: string; files: File[]; coverLetterOnly: boolean }) => void | Promise<void>
  isLoading?: boolean
  initialFiles?: File[]
}

export const UploadScopeModal: React.FC<UploadScopeModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
  initialFiles = []
}) => {
  const [projectName, setProjectName] = useState('')
  const [file, setFile] = useState<File | null>(initialFiles?.[0] || null)
  const [isDragOver, setIsDragOver] = useState(false)
  const [coverLetterOnly, setCoverLetterOnly] = useState(false)

  // Update file when initialFiles changes
  useEffect(() => {
    setFile(initialFiles?.[0] || null)
  }, [initialFiles])

  const handleFileDrop = useCallback((droppedFiles: File[]) => {
    // Only accept the first file
    if (droppedFiles.length > 0) {
      setFile(droppedFiles[0])
    }
  }, [])

  const handleFileRemove = () => {
    setFile(null)
  }

  const handleSubmit = async () => {
    if (projectName.trim() && file) {
      await onSubmit({ projectName: projectName.trim(), files: [file], coverLetterOnly })
      setProjectName('')
      setFile(null)
      setCoverLetterOnly(false)
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    
    const droppedFiles = Array.from(e.dataTransfer.files)
    handleFileDrop(droppedFiles)
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Upload Scope" size="lg">
      <div>
        {/* Project Name Input */}
        <div className="mb-4">
          <label className="text-xs font-semibold block mb-1">
            Project Name
          </label>
          <input
            type="text"
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
            placeholder="Enter project name"
            className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-brand-700"
          />
        </div>

        {/* File Upload Area */}
        <div className="mb-4">
          <label className="text-xs font-semibold block mb-1">
            Upload RFP Document
          </label>

          {/* DS Dropzone — compact variant, per sop-studio */}
          <div
            role="button"
            tabIndex={0}
            className={`group flex flex-col items-center justify-center gap-3 text-center cursor-pointer rounded-xl border-2 border-dashed px-6 py-8 transition-[background-color,border-color,box-shadow] duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-700 focus-visible:ring-offset-2 ${
              isDragOver
                ? 'border-accent-500 bg-accent-100 shadow-md'
                : 'border-accent-300 bg-accent-50/60 hover:border-accent-400 hover:bg-accent-50 hover:shadow-sm'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => {
              const input = document.createElement('input')
              input.type = 'file'
              input.accept = '.pdf,.doc,.docx'
              input.onchange = (e) => {
                const target = e.target as HTMLInputElement
                if (target.files && target.files[0]) {
                  setFile(target.files[0])
                }
              }
              input.click()
            }}
          >
            <div
              className={`flex h-12 w-12 items-center justify-center rounded-full ring-4 ring-accent-50 transition-transform duration-200 ease-out group-hover:-translate-y-0.5 ${
                isDragOver ? 'bg-accent-500 text-white' : 'bg-accent-100 text-accent-600'
              }`}
            >
              <Upload className="w-5 h-5" />
            </div>
            <div className="text-sm font-medium text-slate-700">
              Drag and drop, or{' '}
              <span className="text-accent-600 font-semibold group-hover:underline">browse</span>
            </div>
            <span className="text-[11px] text-slate-500">PDF, DOC, DOCX</span>
          </div>
        </div>

        {/* Uploaded File */}
        {file && (
          <div className="mb-4">
            <h3 className="text-xs font-semibold mb-2">
              Uploaded File
            </h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-md">
                <div className="flex items-center space-x-3">
                  <FileText className="w-8 h-8 text-slate-400" />
                  <div>
                    <p className="text-sm font-medium text-slate-900">{file.name}</p>
                    <p className="text-xs text-slate-500">{formatFileSize(file.size)}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <button
                    onClick={handleFileRemove}
                    className="text-slate-400 hover:text-red-500"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Cover Letter Only Checkbox */}
        <div className="mb-4">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="cover_letter_only"
              checked={coverLetterOnly}
              onChange={(e) => setCoverLetterOnly(e.target.checked)}
              className="w-4 h-4 accent-brand-700 border-slate-300 rounded"
            />
            <label htmlFor="cover_letter_only" className="ml-2 text-sm text-slate-700">
              Cover letter requirements only
            </label>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-2 mt-5 pt-3 border-t border-slate-100">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!projectName.trim() || !file || isLoading}
          >
            {isLoading ? 'Creating Project...' : 'Create Project'}
          </Button>
        </div>
      </div>
    </Modal>
  )
} 